import axios from 'axios';
import {
  getStoredAuthToken,
  hasStoredOwnerAdminSession,
  shouldUseDevOwnerAuth,
} from '../utils/authStorage';

export type CampaignCreativeType = 'text' | 'image' | 'video';

export interface CampaignPackageCreativePricing {
  text?: number;
  image?: number;
  video?: number;
}

export interface CampaignPackage {
  _id: string;
  name: string;
  description?: string;
  basePrice: number;
  displayDurationSeconds?: number;
  creativePricing?: CampaignPackageCreativePricing;
  currency: string;
  minAmount?: number;
  maxAmount?: number;
  city?: string;
  timeOfDay?: string;
  minImpressions?: number;
  maxImpressions?: number;
  active: boolean;
  sortOrder?: number;
}

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.hostname}:5001`
    : '');

const API_ROOT = `${API_BASE.replace(/\/$/, '')}/api`;

export const listPackages = async (amount?: number): Promise<CampaignPackage[]> => {
  const params = new URLSearchParams();
  params.set('active', 'true');
  if (typeof amount === 'number' && Number.isFinite(amount) && amount > 0) {
    params.set('amount', String(amount));
  }

  const res = await fetch(`${API_ROOT}/packages?${params.toString()}`, {
    headers: authHeaders(),
    credentials: 'include',
  });

  if (!res.ok) {
    throw new Error('Failed to load campaign packages');
  }

  return (await res.json()) as CampaignPackage[];
};

export interface CampaignPackageInput {
  name: string;
  description?: string;
  basePrice: number;
  displayDurationSeconds?: number;
  creativePricing?: CampaignPackageCreativePricing;
  currency?: string;
  minAmount?: number;
  maxAmount?: number;
  sortOrder?: number;
}

const isValidPrice = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

export const getPackagePriceForCreative = (
  pkg: CampaignPackage,
  creativeType?: CampaignCreativeType | null,
): number => {
  if (creativeType) {
    const override = pkg.creativePricing?.[creativeType];
    if (isValidPrice(override)) {
      return override;
    }
  }

  return isValidPrice(pkg.basePrice) ? pkg.basePrice : 0;
};

export const getPackageStartingPrice = (pkg: CampaignPackage): number => {
  const prices = [
    pkg.basePrice,
    pkg.creativePricing?.text,
    pkg.creativePricing?.image,
    pkg.creativePricing?.video,
  ].filter(isValidPrice);

  if (!prices.length) {
    return 0;
  }

  return Math.min(...prices);
};

const authHeaders = () => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = getStoredAuthToken();

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (hasStoredOwnerAdminSession() || shouldUseDevOwnerAuth()) {
    headers['X-Tellurvision-Dev-Owner'] = 'true';
  }

  return headers;
};

export const createCampaignPackage = async (
  payload: CampaignPackageInput,
): Promise<CampaignPackage> => {
  const { data } = await axios.post<CampaignPackage>(`${API_ROOT}/packages`, payload, {
    headers: authHeaders(),
  });
  return data;
};

export const updateCampaignPackage = async (
  id: string,
  payload: Partial<CampaignPackageInput>,
): Promise<CampaignPackage> => {
  const { data } = await axios.put<CampaignPackage>(
    `${API_ROOT}/packages/${encodeURIComponent(id)}`,
    payload,
    { headers: authHeaders() },
  );
  return data;
};

export const disableCampaignPackage = async (id: string): Promise<CampaignPackage> => {
  const { data } = await axios.delete<CampaignPackage>(
    `${API_ROOT}/packages/${encodeURIComponent(id)}`,
    { headers: authHeaders() },
  );
  return data;
};

export const listActivePackagesForAdmin = async (): Promise<CampaignPackage[]> => {
  const res = await fetch(`${API_ROOT}/packages?active=true`);
  if (!res.ok) {
    throw new Error('Failed to load campaign packages');
  }
  return (await res.json()) as CampaignPackage[];
};
